import React, { useState } from 'react';
import { 
  FingerPrintIcon, 
  ShieldCheckIcon, 
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface TwoFactorConfig {
  enforceForAllUsers: boolean;
  allowedMethods: string[];
  recoveryCodesCount: number;
  sessionDuration: number;
  graceLoginPeriod: number;
}

export const Configure2FAPage: React.FC = () => {
  const [config, setConfig] = useState<TwoFactorConfig>({
    enforceForAllUsers: false,
    allowedMethods: ['authenticator', 'sms'],
    recoveryCodesCount: 8,
    sessionDuration: 30,
    graceLoginPeriod: 7
  });

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <FingerPrintIcon className="w-8 h-8 text-[#FF5722] mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication Configuration</h1>
        </div>

        {/* General Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">General Settings</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">Enforce 2FA for All Users</h3>
                <p className="text-sm text-gray-600">Require all users to set up 2FA upon next login</p>
              </div>
              <label className="flex items-center">
                <input 
                  type="checkbox"
                  checked={config.enforceForAllUsers}
                  onChange={(e) => setConfig({...config, enforceForAllUsers: e.target.checked})}
                  className="form-checkbox text-[#FF5722] rounded"
                />
              </label>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Authentication Methods</h3>
              <div className="space-y-2">
                {['authenticator', 'sms', 'email', 'security_key'].map((method) => (
                  <label key={method} className="flex items-center">
                    <input 
                      type="checkbox"
                      checked={config.allowedMethods.includes(method)}
                      onChange={(e) => {
                        const newMethods = e.target.checked 
                          ? [...config.allowedMethods, method]
                          : config.allowedMethods.filter(m => m !== method);
                        setConfig({...config, allowedMethods: newMethods});
                      }}
                      className="form-checkbox text-[#FF5722] rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">
                      {method.replace('_', ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recovery Options */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Recovery Options</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Recovery Codes</h3>
              <div className="flex items-center space-x-4">
                <input 
                  type="number"
                  value={config.recoveryCodesCount}
                  onChange={(e) => setConfig({...config, recoveryCodesCount: parseInt(e.target.value)})}
                  className="border rounded-md p-2 w-24"
                  min="4"
                  max="12"
                />
                <span className="text-sm text-gray-600">Number of recovery codes per user</span>
              </div>
            </div>
          </div>
        </div>

        {/* Session Management */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Session Management</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">2FA Session Duration</h3>
              <div className="flex items-center space-x-4">
                <input 
                  type="number"
                  value={config.sessionDuration}
                  onChange={(e) => setConfig({...config, sessionDuration: parseInt(e.target.value)})}
                  className="border rounded-md p-2 w-24"
                  min="1"
                />
                <span className="text-sm text-gray-600">Days before requiring 2FA verification again</span>
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Grace Period</h3>
              <div className="flex items-center space-x-4">
                <input 
                  type="number"
                  value={config.graceLoginPeriod}
                  onChange={(e) => setConfig({...config, graceLoginPeriod: parseInt(e.target.value)})}
                  className="border rounded-md p-2 w-24"
                  min="0"
                />
                <span className="text-sm text-gray-600">Days to allow login without 2FA after enabling requirement</span>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button className="bg-[#FF5722] text-white px-6 py-2 rounded-md hover:bg-[#F4511E]">
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default Configure2FAPage; 